import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Button, InputField } from 'src/components';
import { generateUniqueDeviceId } from 'src/components/Common/UniqueUserIdGenerator';
import { useGetLookupDataQuery } from 'src/rtk/features/setup/lookup/lookupDataApi';
import { useCreateLogbookEntryMutation } from 'src/rtk/features/trainee/traineeLogbookEntryApi';
import * as Yup from 'yup';
import loader from '../../../../assets/images/loader.gif';
import UpdatedCompetencyGroupAndDetails from './UpdatedCompetencyGroupAndDetails';

interface Field {
  id: string;
  name: string;
  placeholder?: string;
  type: string;
  options?: LogbookEntryDetails[];
  required?: number;
}

const DynamicForm = (props: any) => {
  const {
    dynamicFormProps,
    entrySetupMasterId,
    formName,
    caseTypeName,
    skillTypeName,
    competencyGroup,
    handleFormAndCaseReset,
    setForm,
  } = props;

  const [reRenderCompetency, setReRenderCompetency] = useState<number>(0);

  const history = useHistory();

  const InputFields: Field[] = dynamicFormProps.map(
    (field: any, index: number) => {
      return {
        id: field.id,
        name: `${field['column_name']?.replace('.', '')}`,
        placeholder: field.details_text,
        type: field.column_value_type,
        options: field.options,
        required: field.required_ind,
      };
    },
  );

  const [submitIndicator, setSubmitIndicator] = useState<number>(1);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [uniqueDeviceId, setUniqueDeviceId] = useState<string>('');

  //   const { data: form, isError: isFormError } = useGetLogBookMasterSetupQuery(formId);
  //   const { data: caseType, isError: isCaseTypeError } = useGetLogbookEntrySetupMasterQuery(caseTypeId);
  //   const { data: skillType, isError: isSkillTypeError } = useGetLogbookEntrySetupMasterQuery(skillTypeId);

  const [
    createLogbookEntry,
    {
      data: logbookEntry,
      error: logbookEntryError,
      isSuccess: isLogbookEntrySuccess,
      isError: isLogbookEntryError,
      isLoading: isLogbookEntryLoading,
    },
  ] = useCreateLogbookEntryMutation();

  const competenceLevelOptions: LookupData[] = useGetLookupDataQuery('competence_level').data?.data[0]?.childs!;

  type FormValues = {
    [key: string]: string | number | boolean | undefined;
  };

  const entryError: CustomError = logbookEntryError as CustomError;

  const initialValues = React.useMemo(() => {
    const fieldsObj = InputFields.reduce((acc: FormValues, field: Field) => {
      switch (field.type) {
        case 'text':
        case 'textarea':
        case 'email':
        case 'date':
        case 'file':
          acc[field.name] = '';
          break;
        case 'number':
          acc[field.name] = 0;
          break;
        case 'checkbox':
          acc[field.name] = false;
          break;
        case 'select':
          if (field.options?.length! > 0) {
            acc[field.name] = '';
          }
          break;
        case 'radio':
          if (field.options?.length! > 0) {
            acc[field.name] = '';
          }
          break;
        default:
          acc[field.name] = undefined;
          break;
      }

      return acc;
    }, {});

    return fieldsObj;
  }, [InputFields]);

  // const initialErrors = React.useMemo(() => {
  //   const fieldsObj = InputFields.reduce((acc: any, field: Field) => {
  //       acc[field.name] = '';
  //       return acc;
  //     }, {});

  //   return fieldsObj;
  // }, [InputFields]);

  const generateValidationSchema = (inputFields: any) => {
    const validationSchema: Record<string, any> = {};

    inputFields.forEach((field: any) => {
      if (
        field.required &&
        (field.type === 'text' ||
          field.type === 'textarea' ||
          field.type === 'date' ||
          field.type === 'email')
      ) {
        validationSchema[field['name']] = Yup.string().required(
          `${field['name']} is required`,
        );
      } else if (field.required && field.type === 'select') {
        if (field.options?.length! > 0) {
          validationSchema[field['name']] = Yup.string().required(
            `${field['name']} is required`,
          );
        }
      } else if (field.required && field.type === 'radio') {
        if (field.options?.length! > 0) {
          validationSchema[field['name']] = Yup.string().required(
            `${field['name']} is required`,
          );
        }
      } else if (field.required && field.type === 'file') {
        validationSchema[field['name']] = Yup.string().required(`${field['name']} is required`);
      }
    });

    return Yup.object().shape(validationSchema);
  };

  const getBase64 = (file: any) => new Promise((resolve, reject) => {
    var reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

  const decodeBase64EncodeString = (base64String: string) => {
    let correctBase64EncodedString: string = base64String.split("base64,")?.[1];
    let tempString: string = base64String.split("base64,")?.[0];
    let typeOfDocument: string = tempString.split("data:")?.[1] + ';base64';
    let byteCharacters = atob(correctBase64EncodedString);
    let byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    let byteArray = new Uint8Array(byteNumbers);
    let file = new Blob([byteArray], { type: typeOfDocument });
    let fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  };  

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema:
      InputFields.length > 0 ? generateValidationSchema(InputFields) : null,
    onSubmit: (values) => {
      setSubmitted(true);
      const allKeys: any = Object.keys(initialValues);
      const allValues: any = Object.keys(values);
      allKeys.forEach((key: any) => {
        if (!allValues.includes(key)) {
          values[key] = initialValues[key];
        }
      });

      console.log(values);

      const sortedValues: any = {};
      InputFields?.forEach((i: Field) => {
        sortedValues[i?.name] = values[i?.name];
      });

      const finalSubmittedValue = {
        heading: formName,
        entry_setup_master_id: entrySetupMasterId,
        details_json: JSON.stringify(
          {
            entry_setup_master_id: entrySetupMasterId,
            form: formName,
            caseType: caseTypeName,
            skillType: skillTypeName,
            ...sortedValues,
          },
          null,
          2,
        ),
        competency_group: values?.competency_group ?? [],
        submit_ind: submitIndicator,
        device_info: uniqueDeviceId,
        status: 1,
        log_type: 1,
      };

      createLogbookEntry({
        ...finalSubmittedValue,
      });

      // alert(JSON.stringify(values, null, 2));
    },
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    handleReset,
    setValues,
    setFieldValue,
    getFieldProps,
    values,
    touched,
    errors,
    resetForm,
    setErrors,
  } = formik;

  useEffect(() => {
    const generateDeviceId: Promise<string> = generateUniqueDeviceId();
    generateDeviceId.then((value: string) => {
      setUniqueDeviceId(value);
    });
  }, []);

  useEffect(() => {
    dynamicFormProps?.forEach((field: any) => {
      if (field.column_value_type === 'date') {
        setFieldValue(`${field['column_name']}`, new Date(Date.now()).toISOString().slice(0, 10));
      }
    });
  }, [dynamicFormProps]);

  useEffect(() => {
    if (isLogbookEntrySuccess) {
      setSubmitted(false);
      setValues(initialValues);
      toast.success('Logbook entry successful');
      history.push('/trainee/logbook-entry');
    }
    if (isLogbookEntryError) {
      setSubmitted(false);
      toast.error(entryError?.message || 'Something went wrong!');
    }
  }, [isLogbookEntrySuccess, isLogbookEntryError]);

  useEffect(() => {
    if (isLogbookEntrySuccess) {
      const allKeys: any = Object.keys(initialValues);
      allKeys.forEach((key: any) => {
        errors[key] = '';
      });
    }
  }, [isLogbookEntrySuccess, values]);

  return (
    <>
      {isLogbookEntryLoading ? (
        <div className="overlay">
          <img src={loader} alt="Loading" height={100} width={300} />
        </div>
      ) : null}
      <Form
        className="custom-form pt-3 mt-3"
        onSubmit={(e: any) => {
          e.preventDefault();
          handleSubmit();
        }}
        onReset={(e: any) => {
          e.preventDefault();
          setForm({ id: '' });
          handleReset(e);
        }}
      >
        <div className="row">
          {InputFields?.map((field, index) => {
            switch (field.type) {
              case 'checkbox':
                return (
                  <div key={field.id} className="col-sm-12">
                    <FormGroup check className="mb-3 mt-4">
                      <Label check>
                        <Input
                          type={field.type}
                          checked={values[field['name']] as boolean}
                          {...getFieldProps(field['name'])}
                        />{' '}
                        {field['name']}
                      </Label>
                    </FormGroup>
                  </div>
                );
              case 'radio':
                return field.options?.length! > 0 ? (
                  <div key={field.id} className="col-sm-12">
                    <Label className={field.required ? "required-field form-label" : "form-label"}>{field['name']}</Label>
                    <br />
                    {field.options?.map((option: any) => {
                      return (
                        <FormGroup check inline key={option.id}>
                          <Input
                            id={option.column_name}
                            name={field['name']}
                            type={field.type as InputType}
                            value={option.details_text}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                          />

                          <Label check for={option.column_name}>
                            {option.column_name}
                          </Label>
                        </FormGroup>
                      );
                    })}
                  </div>
                ) : null;
              case 'file':
                return (
                  <div key={field.id} className="col-lg-4 col-md-6 col-sm-12">
                    <FormGroup className="mb-3">
                      <Label className={field.required ? "required-field form-label me-2" : "form-label me-2"}>{field['name']} (PDF file)</Label>
                      {typeof values?.[field['name']] === 'string' &&
                        values?.[field['name']] !== '' ? (
                        <a
                          className='btn btn-sm btn-outline-info my-2'
                          rel='noreferrer'
                          target='_blank'
                          onClick={(e) => {
                            decodeBase64EncodeString(values?.[field['name']] as string);
                          }}
                        >
                          View
                        </a>
                      ) : (
                        null
                      )}
                      <InputField
                        type={field.type as InputType}
                        name={field['name']}
                        placeholder={field.placeholder}
                        onChange={(e) => {
                          getBase64(e.target?.files[0]).then((value) => {
                            setFieldValue(field['name'], value);
                            // console.log(value);
                          }).catch((err) => {
                            setFieldValue(field['name'], '');
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={{ errors, touched }}
                      />
                    </FormGroup>
                  </div>
                );
              case 'select':
                return field.options?.length! > 0 ? (
                  <div key={field.id} className="col-lg-4 col-md-6 col-sm-12">
                    <FormGroup className="mb-3">
                      <Label for={field['name']} className={field.required ? "required-field form-label" : "form-label"}>{field['name']}</Label>
                      <InputField
                        type={field.type as InputType}
                        placeholder={field.placeholder}
                        options={field.options?.map((option: any) => {
                          return {
                            label: option.column_name,
                            value: option.details_text,
                          };
                        })}
                        invalid={{ errors, touched }}
                        {...getFieldProps(field['name'])}
                      />
                    </FormGroup>
                  </div>
                ) : null;
              case 'date':
                return (
                  ['date of birth', 'dob'].includes(field['name'].toLowerCase()) ?
                    <div key={field.id} className="col-lg-4 col-md-6 col-sm-12">
                      <FormGroup className="mb-3">
                        <Label for={field['name']} className={field.required ? "required-field form-label" : "form-label"}>{field['name']}</Label>
                        <InputField
                          type={field.type as InputType}
                          placeholder={field.placeholder}                          
                          invalid={{ errors, touched }}
                          {...getFieldProps(`${field['name']}`)}
                        />
                      </FormGroup>
                    </div>
                    :
                    <div key={field.id} className="col-lg-4 col-md-6 col-sm-12">
                      <FormGroup className="mb-3">
                        <Label for={field['name']} className={field.required ? "required-field form-label" : "form-label"}>{field['name']}</Label>
                        <InputField
                          type={field.type as InputType}
                          placeholder={field.placeholder}
                          // min={new Date(Date.now()).toISOString().slice(0, 10)}
                          invalid={{ errors, touched }}
                          {...getFieldProps(`${field['name']}`)}
                        />
                      </FormGroup>
                    </div>
                );
              default:
                return (
                  <div key={field.id} className="col-lg-4 col-md-6 col-sm-12">
                    <FormGroup className="mb-3">
                      <Label for={field['name']} className={field.required ? "required-field form-label" : "form-label"}>{field['name']}</Label>
                      <InputField
                        type={field.type as InputType}
                        placeholder={field.placeholder}
                        invalid={{ errors, touched }}
                        {...getFieldProps(`${field['name']}`)}
                      />
                    </FormGroup>
                  </div>
                );
            }
          })}

          <div className="col-sm-12">
            {competencyGroup?.length > 0 ? (
              <UpdatedCompetencyGroupAndDetails
                key={reRenderCompetency}
                formik={formik}
                caseTypeName={caseTypeName}
                skillTypeName={skillTypeName}
                compentencyGroup={competencyGroup}
                competenceLevelOptions={competenceLevelOptions}
              />
            ) : null}
          </div>

          <div className="col-sm-12">
            <FormGroup className="mb-3 text-center">
              <Button
                type="reset"
                text="Reset"
                className="btn btn-secondary me-2"
                onClick={(e) => {
                  setValues(initialValues);
                  handleFormAndCaseReset();
                  setReRenderCompetency(1);
                }}
                disabled={InputFields?.length === 0}
              ></Button>
              {!submitted && InputFields?.length > 0 ? (
                <>
                  <Button
                    type="submit"
                    text="Save as Draft"
                    className="btn btn-info me-2"
                    onClick={(e) => {
                      setSubmitIndicator(0);
                      console.log(errors);
                    }}
                  ></Button>
                  <Button
                    type="submit"
                    text="Submit"
                    className="btn btn-success"
                    onClick={(e) => {
                      setSubmitIndicator(1);
                    }}
                  ></Button>
                </>
              ) : (
                <>
                  <Button
                    type="submit"
                    text="Save as Draft"
                    className="btn btn-info me-2"
                    onClick={(e) => {
                      setSubmitIndicator(0);
                      console.log(errors);
                    }}
                    disabled
                  ></Button>
                  <Button
                    type="submit"
                    text="Submit"
                    className="btn btn-success"
                    onClick={(e) => {
                      setSubmitIndicator(1);
                    }}
                    disabled
                  ></Button>
                </>
              )}
            </FormGroup>
          </div>
        </div>
      </Form>
    </>
  );
};

export default DynamicForm;
